import { useEffect } from 'react';

/**
 * ⌘Z / Ctrl+Z to undo, with Shift or Y to redo (FR-052).
 *
 * Bound on `window` rather than on the canvas, because undo is about the
 * document and not about whichever element happens to hold focus. A user who
 * has just clicked a button in the inspector still means "undo my last edit".
 *
 * **Suppressed inside text fields**, which is the part that matters. A textarea
 * has its own undo stack covering the characters typed into it, and stealing
 * ⌘Z there would make the browser's native behaviour unavailable exactly where
 * it is most expected — mid-sentence in a prompt.
 */
export function useUndoShortcuts({
  onUndo,
  onRedo,
}: {
  onUndo: () => void;
  onRedo: () => void;
}): void {
  useEffect(() => {
    const handle = (event: KeyboardEvent) => {
      if (!event.metaKey && !event.ctrlKey) return;

      const key = event.key.toLowerCase();
      if (key !== 'z' && key !== 'y') return;
      if (isTextEntry(event.target)) return;

      event.preventDefault();
      // ⌘⇧Z and Ctrl+Y are both redo; the first is the Mac convention, the
      // second the Windows one, and supporting only one would be wrong for half
      // the users.
      if (key === 'y' || event.shiftKey) onRedo();
      else onUndo();
    };

    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [onUndo, onRedo]);
}

/** Whether the event came from somewhere with its own undo stack. */
function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;

  const tag = target.tagName;
  if (tag === 'TEXTAREA') return true;
  if (tag !== 'INPUT') return false;

  // A checkbox or a button-like input has no text to undo, so the document's
  // undo is the right one there.
  const type = (target as HTMLInputElement).type;
  return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range'].includes(
    type,
  );
}
